<template>
	<div>
		<div class="fs4 fw7 mb1 lh125">{{ $t("facts") }}</div>

		<!-- Select facts template -->
		<core-form-input
			name="factsTemplate"
			:placeholder="$t('select_facts_template')"
			componentName="j-base-select"
			valueKey="id"
			v-model="form.factsTemplateId"
			:options="options.factsTemplates"
			@focus="fetchFactsTemplates"
			@input="setFacts"
		/>

		<!-- Display all facts -->
		<div class="mt4 flex wrap bg4 p4 r2 bc7 b1" v-if="form.facts.length">
			<div v-for="(item, i) in form.facts" :key="i" class="flex wrap xs-12 mb5">
				<div class="flex wrap xs-12 xs-bottom xs-between">
					<div class="xs">
						<core-form-input
							:name="item.name"
							size="small"
							:label="item.name"
							:componentName="item.componentName"
							v-model="item.value"
							:options="item.options"
						/>
					</div>
					<crm-icon
						name="delete"
						color="cError"
						size="small"
						class="cu2 mb1 ml2"
						@click.native="onRemove(item.type)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CrmIcon from "@/components/CrmIcon"

import api from "../api/"
import dayjs from "dayjs"

export default {
	components: { CoreFormInput, CrmIcon },
	data() {
		return {
			form: {
				factsTemplateId: null,
				facts: []
			},
			options: {
				factsTemplates: []
			}
		}
	},

	methods: {
		setFacts() {
			// Get list of facts
			let facts =
				this.options.factsTemplates.find(
					item => item.id === this.form.factsTemplateId
				)?.possibleValueTemplates || []

			// Parse values so taht we can determine what UI to display
			facts = facts.map(item => ({
				name: item.factName,
				type: item.factType, // it's something like id
				value: null, // here we save value from the form
				...this.parseFact(item.possibleValues) // attach some properties for UI construction
			}))

			this.form.facts = facts
		},

		/**
		 * @description It will parse possibleValues of fact and return schema for UI
		 */
		parseFact(possibleValues = []) {
			if (possibleValues.length === 0) return {}

			// NOTE: we are parsing only first item of the array
			// Not exactly sure what should happen when there is more possibleValues
			const toParse = possibleValues[0]

			// Helper function to decide component type
			const decideComponent = ({ scalarType = "String", values = [] }) => {
				if (values && values.length > 0) return "j-base-select"
				if (scalarType === "String") return "j-base-input-text"
				if (scalarType === "Number") return "j-base-input-number"
				if (scalarType === "Boolean") return "j-base-toggle"
				if (scalarType === "Date") return "j-base-date-picker-simple"
			}

			// Helper function to extract options from toParse.values
			const parseOptions = ({ values = [] }) => {
				if (!values) return []
				return values.map(scalarType => {
					const value = Object.values(scalarType).find(item => item !== null)
					return {
						name: value,
						value
					}
				})
			}

			return {
				dataType: toParse.scalarType, // dataType so that we can convert data properly
				componentName: decideComponent(toParse), // so that we redner proper UI
				options: parseOptions(toParse) // if there are options, render them in UI
			}
		},

		/**
		 * @description We have to convert fact.value based on fact.dataType
		 */
		// TODO: maybe we don't need to do it here but in parent because we will be sending scalarValue to API anyway so we also have to convert that
		processFactsValues() {
			const convertValueToProperDataType = ({
				dataType = "String",
				value = null
			}) => {
				if (!value) return null
				if (dataType === "String") return value.toString()
				if (dataType === "Number") return Number(value)
				if (dataType === "Boolean") {
					if (value === true) return true
					if (value === false) return false
					if (value.toLowerCase === "true") return true
					if (value.toLowerCase === "false") return false
				}
				if (dataType === "Date") return dayjs(value).format("YYYY-MM-DD")
			}

			return this.form.facts.map(item => ({
				...item,
				value: convertValueToProperDataType(item)
			}))
		},

		/**
		 * @description Fetches all factTemplates only if form.options are not populated yet
		 */
		async fetchFactsTemplates() {
			if (this.options.factsTemplates.length > 0) return // prevent refetching when options are already populated
			const res = await api.factsTemplates()
			if (res.errors) return
			this.options.factsTemplates = res.data.claimFactTemplates
		},

		onRemove(type) {
			// Find fact in array
			if (!type) return
			let index
			this.form.facts.find((item, i) => {
				if (item.type === type) index = i
			})

			if (index === null) return
			this.form.facts.splice(index, 1)
		}
	},

	created() {
		this.fetchFactsTemplates()
	},

	watch: {
		/**
		 * @description Anytime fact is updated, emit input event so that parent can save the latest version
		 */
		"form.facts": {
			handler() {
				this.$emit("input", this.processFactsValues())
			},
			immediate: false,
			deep: true
		}
	}
}
</script>
